import type { AdminAccountRole, AdminPermissions } from '../types/admin-permissions.types';

export const FULL_ADMIN_PERMISSIONS: AdminPermissions = {
  sections: {
    sessions: true,
    programs: true,
    criterias: true,
    impacts: true,
    verbatims: true,
    playerTexts: true,
  },
  actions: {
    sessionsView: true,
    sessionsCreate: true,
    sessionsEdit: true,
    sessionsArchive: true,
    sessionsDelete: true,
    programsView: true,
    programsCreate: true,
    programsEdit: true,
    programsDelete: true,
    contentEdit: true,
    manageSubAccounts: true,
  },
};

/** Base vide pour les opérateurs : aucun droit tant qu'il n'est pas coché explicitement. */
export const EMPTY_OPERATOR_PERMISSIONS: AdminPermissions = {
  sections: {
    sessions: false,
    programs: false,
    criterias: false,
    impacts: false,
    verbatims: false,
    playerTexts: false,
  },
  actions: {
    sessionsView: false,
    sessionsCreate: false,
    sessionsEdit: false,
    sessionsArchive: false,
    sessionsDelete: false,
    programsView: false,
    programsCreate: false,
    programsEdit: false,
    programsDelete: false,
    contentEdit: false,
    manageSubAccounts: false,
  },
};

/** Preset utilisé pour pré-remplir le formulaire de création d'opérateur. */
export const DEFAULT_OPERATOR_PERMISSIONS: AdminPermissions = {
  sections: {
    sessions: true,
    programs: true,
    criterias: false,
    impacts: false,
    verbatims: false,
    playerTexts: false,
  },
  actions: {
    sessionsView: true,
    sessionsCreate: true,
    sessionsEdit: true,
    sessionsArchive: false,
    sessionsDelete: false,
    programsView: true,
    programsCreate: true,
    programsEdit: true,
    programsDelete: false,
    contentEdit: false,
    manageSubAccounts: false,
  },
};

export const FACILITATOR_PERMISSIONS: AdminPermissions = {
  sections: {
    sessions: true,
    programs: false,
    criterias: false,
    impacts: false,
    verbatims: false,
    playerTexts: false,
  },
  actions: {
    sessionsView: true,
    sessionsCreate: false,
    sessionsEdit: false,
    sessionsArchive: false,
    sessionsDelete: false,
    programsView: false,
    programsCreate: false,
    programsEdit: false,
    programsDelete: false,
    contentEdit: false,
    manageSubAccounts: false,
  },
};

export const ADMIN_SECTION_LABELS: Record<keyof AdminPermissions['sections'], string> = {
  sessions: 'Sessions',
  programs: 'Programmes',
  criterias: 'Critères',
  impacts: 'Impacts',
  verbatims: 'Verbatims',
  playerTexts: 'Textes joueur',
};

export const ADMIN_ACTION_LABELS: Record<keyof AdminPermissions['actions'], string> = {
  sessionsView: 'Voir les sessions',
  sessionsCreate: 'Créer des sessions',
  sessionsEdit: 'Modifier des sessions',
  sessionsArchive: 'Archiver des sessions',
  sessionsDelete: 'Supprimer des sessions',
  programsView: 'Voir les programmes',
  programsCreate: 'Créer des programmes',
  programsEdit: 'Modifier des programmes',
  programsDelete: 'Supprimer des programmes',
  contentEdit: 'Modifier le contenu (critères, impacts, verbatims, textes)',
  manageSubAccounts: 'Gérer les sous-comptes',
};

export const ADMIN_ROLE_LABELS: Record<AdminAccountRole, string> = {
  super_admin: 'Super admin',
  admin: 'Admin',
  operator: 'Opérateur (droits personnalisés)',
  facilitator: 'Facilitateur / animateur',
};

export const resolveRoleFromFlags = (isSuperAdmin: boolean, isAdmin: boolean, role?: string | null): AdminAccountRole => {
  if (isSuperAdmin) return 'super_admin';
  if (isAdmin) return 'admin';
  if (role === 'facilitator' || role === 'operator') return role;
  return 'operator';
};

export const SECTION_VIEW_ACTION: Partial<Record<keyof AdminPermissions['sections'], keyof AdminPermissions['actions']>> = {
  sessions: 'sessionsView',
  programs: 'programsView',
};

export const SECTION_ACTIONS: Partial<
  Record<keyof AdminPermissions['sections'], Array<keyof AdminPermissions['actions']>>
> = {
  sessions: ['sessionsView', 'sessionsCreate', 'sessionsEdit', 'sessionsArchive', 'sessionsDelete'],
  programs: ['programsView', 'programsCreate', 'programsEdit', 'programsDelete'],
  criterias: ['contentEdit'],
  impacts: ['contentEdit'],
  verbatims: ['contentEdit'],
  playerTexts: ['contentEdit'],
};
